import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from 'src/services/auth.service';
import { getLogger } from 'src/utils/logger';
import { SmartGoal, GenerationResult } from 'src/models/form-types';
import { GENERIC_ERROR_MESSAGE } from 'src/utils/errorMessages';

const logger = getLogger('useSmartGoalsGeneration');

export function useSmartGoalsGeneration<FormData>(
  generateService: (data: FormData, token: string) => Promise<any>,
  formData: FormData
) {
  const [generationState, setGenerationState] = useState<GenerationResult<SmartGoal[]>>({
    isLoading: false,
    error: null,
    data: null
  });
  
  const [selectedGoal, setSelectedGoal] = useState<string | null>(null);
  const { isAuthenticated } = useAuth0();
  const { callApi } = useAuthenticatedApi();

  const generateSmartGoals = useCallback(async () => {
    setGenerationState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));
    
    try {
      if (isAuthenticated) {
        const { data, error } = await callApi(generateService, formData);
        
        if (error) {
          logger.error('Error generating smart goals:', error);
          setGenerationState({
            isLoading: false,
            error: error.message || 'An error occurred while generating SMART goals',
            data: null
          });
        } else if (data?.goals) {
          logger.debug(`Smart goals: ${JSON.stringify(data.goals)}`);
          
          // Map the goals array to SmartGoal objects
          const formattedGoals: SmartGoal[] = data.goals.map((goal: string, index: number) => ({
            id: index + 1,
            text: goal
          }));
          
          setGenerationState({
            isLoading: false,
            error: null,
            data: formattedGoals
          });
        } else {
          setGenerationState({
            isLoading: false,
            error: 'No goals returned from the service',
            data: null
          });
        }
      } else {
        setGenerationState({
          isLoading: false,
          error: 'Authentication required to generate goals',
          data: null
        });
      }
    } catch (error) {
      logger.error('Exception generating smart goals:', error);
      setGenerationState({
        isLoading: false, 
        error: GENERIC_ERROR_MESSAGE,
        data: null
      });
    }
  }, [isAuthenticated, callApi, generateService, formData]);

  const selectGoal = useCallback((goal: string) => {
    setSelectedGoal(goal);
  }, []);

  return {
    goals: generationState.data || [],
    isLoading: generationState.isLoading,
    error: generationState.error,
    selectedGoal,
    generateSmartGoals,
    selectGoal
  };
}